import axios from 'axios';

import appSettings from '../app-settings';

const API_URL = `${appSettings.baseUrl}`;

/**
 * List the tenant's image library. Empty params are omitted so the default call
 * is a stable URL, which is what demo-mode mocking matches on.
 */
export const getGalleryImages = async ({ page = 1, perPage = 24, search = '', filter = '' } = {}) => {
  const params = { page, perPage };
  if (search) params.search = search;
  if (filter) params.filter = filter;

  const response = await axios.get(`${API_URL}/campaigns/images`, { params });
  return response.data;
};

/**
 * Request a presigned POST for a gallery upload. The server pins the key and
 * content type, and returns the `pending` image record to confirm afterwards.
 */
export const createGalleryImageUploadUrl = async ({ filename, contentType, size, width, height }) => {
  const response = await axios.post(`${API_URL}/campaigns/images`, {
    filename,
    contentType,
    size,
    width,
    height,
  });
  return response.data;
};

/**
 * POST the file to S3. Unlike the older campaign-image upload this does NOT
 * rename the file: the key is fixed server-side and arrives in `fields.key`, so
 * a client-side rename would be ignored at best.
 */
export const uploadGalleryImage = async (uploadUrl, fields, file, onProgress) => {
  await axios.postForm(
    uploadUrl,
    { ...fields, file },
    {
      headers: { Authorization: '' },
      onUploadProgress: ({ loaded, total }) => {
        if (!onProgress || !total) return;
        onProgress(Math.round((loaded / total) * 100));
      },
    },
  );
};

/** Edit the description and/or confirm a completed upload. */
export const updateGalleryImage = async (imageId, { description, confirmed } = {}) => {
  const body = {};
  if (description !== undefined) body.description = description;
  if (confirmed !== undefined) body.confirmed = confirmed;

  const response = await axios.patch(`${API_URL}/campaigns/images/${imageId}`, body);
  return response.data;
};

export const deleteGalleryImage = async (imageId) => {
  await axios.delete(`${API_URL}/campaigns/images/${imageId}`);
};

/**
 * Ask the server to describe an image with AI and save the result.
 *
 * Safe to call right after every upload: the server will not overwrite a
 * description the tenant wrote. `force` is for the explicit regenerate action,
 * which replaces an existing AI description.
 */
export const generateGalleryImageDescription = async (imageId, { force = false } = {}) => {
  const response = await axios.post(`${API_URL}/campaigns/images/${imageId}/description`, { force });
  return response.data;
};
